/*! Copyright © 2026 Kontextlab GmbH. All rights reserved. */

/*
 * Turns a plain list into a one-at-a-time image gallery.
 *
 * Authors write a gallery as an ordinary `<ul>` of images or embed links
 * and mark it with a preceding `.start-gallery`. The list is still that
 * list without scripting, which is what a crawler and a reader with no
 * JavaScript get: every image, in order.
 *
 * Both article layouts carried their own copy of this as inline jQuery.
 * It is one module now, because two copies of one thing is how most of
 * the faults in the Map Viewer began.
 */

import { t } from './i18n.js';

/** Marks an embed link: authors write the word in the link text. */
const EMBED_TEXT = 'embedded';

/**
 * Read `[name='value']` pairs an author put in a title attribute.
 *
 * The Map Builder has no field for iframe attributes, so they are written into
 * the title and stripped back out here.
 *
 * @param {string} title - The raw title attribute.
 * @returns {{attributes: object, title: string}} The pairs, and what is
 *   left of the title once they are removed.
 */
export function readEmbedAttributes(title) {
    const attributes = {};
    const rest = (title || '').replace(
        /\[([a-z][\w-]*)='([^']*)']/gi,
        (match, name, value) => {
            attributes[name] = value;
            return '';
        }
    );
    return { attributes, title: rest.trim() };
}

/**
 * Replace an embed link with the iframe it describes.
 *
 * @param {HTMLAnchorElement} link - The link to replace.
 * @returns {HTMLElement} The wrapper that took its place.
 */
function embedToIframe(link) {
    const { attributes, title } = readEmbedAttributes(
        link.getAttribute('title')
    );

    const frame = document.createElement('iframe');
    frame.src = link.getAttribute('href');
    Object.entries(attributes).forEach(([name, value]) => {
        frame.setAttribute(name, value);
    });

    // The wrapper reserves the embed's aspect ratio so the page does not
    // jump when the frame loads. A missing or unusable size falls back to
    // 16:9 rather than collapsing the box to nothing.
    const width = Number.parseFloat(attributes.width);
    const height = Number.parseFloat(attributes.height);
    const ratio =
        width > 0 && height > 0 ? (height * 100) / width : (9 * 100) / 16;

    const wrapper = document.createElement('div');
    wrapper.className = 'ratio-wrapper';
    wrapper.style.paddingTop = `${ratio}%`;
    wrapper.setAttribute('title', title);
    wrapper.appendChild(frame);

    link.replaceWith(wrapper);
    return wrapper;
}

/**
 * Build one navigation control.
 *
 * @param {string} href - Fragment the control points at.
 * @param {string} icon - Icon class.
 * @param {string} label - The control's accessible name.
 * @returns {HTMLAnchorElement} The control.
 */
function navControl(href, icon, label) {
    const link = document.createElement('a');
    link.href = href;
    const symbol = document.createElement('i');
    symbol.className = icon;
    symbol.setAttribute('aria-hidden', 'true');
    // The name is text rather than an `aria-label`: a label never reaches
    // whoever reviews the translations.
    const name = document.createElement('span');
    name.className = 'sr-only';
    name.textContent = label;
    link.append(symbol, name);
    return link;
}

/**
 * Turn a list into a gallery.
 *
 * @param {HTMLElement} list - The `<ul>` holding the images.
 * @returns {object|null} A controller, or nothing if there is none to build.
 */
export function createGallery(list) {
    if (!list) return null;
    // Building twice would wrap every figure inside another one.
    if (list.dataset.gallery === 'ready') return null;

    const items = [...list.children];
    if (!items.length) return null;

    list.classList.add('gallery-container');
    list.dataset.gallery = 'ready';

    items.forEach((item) => {
        let content = [...item.children].find(
            (child) =>
                child.tagName === 'IMG' ||
                (child.tagName === 'A' &&
                    (child.textContent || '').includes(EMBED_TEXT))
        );
        if (!content) return;

        const figure = document.createElement('figure');
        content.replaceWith(figure);
        figure.appendChild(content);

        if (content.tagName === 'A') {
            content = embedToIframe(content);
        }

        const caption = document.createElement('figcaption');
        caption.textContent = content.getAttribute('title') || '';
        content.setAttribute('title', '');
        content.after(caption);

        // The caption now describes the image, so one the author left
        // without alt text is decorative rather than unlabelled.
        const images = [
            ...(content.tagName === 'IMG' ? [content] : []),
            ...content.querySelectorAll('img'),
        ];
        images.forEach((image) => {
            if (image.getAttribute('alt') === null) image.setAttribute('alt', '');
        });
    });

    // Which image of how many, announced rather than only drawn: the
    // controls alone leave a screen-reader user with no idea whether
    // anything happened.
    const position = document.createElement('span');
    position.className = 'sr-only';
    position.setAttribute('aria-live', 'polite');

    let current = 0;
    // `hidden`, not opacity: an image faded out is still in the tab order
    // and still read aloud.
    const show = (index) => {
        current = index;
        items.forEach((item, i) => {
            item.hidden = i !== index;
        });
        position.textContent = t('gallery.position', {
            index: index + 1,
            total: items.length,
        });
    };

    const nav = document.createElement('li');
    nav.className = 'gallery-nav';

    const previous = navControl(
        '#prev',
        'fas fa-chevron-left fa-fw',
        t('gallery.previous')
    );
    const next = navControl(
        '#next',
        'fas fa-chevron-right fa-fw',
        t('gallery.next')
    );

    previous.addEventListener('click', (event) => {
        event.preventDefault();
        if (current > 0) show(current - 1);
    });
    next.addEventListener('click', (event) => {
        event.preventDefault();
        if (current < items.length - 1) show(current + 1);
    });

    nav.append(previous, position, next);
    list.appendChild(nav);
    show(0);

    return {
        /** Show one image. */
        show,
        /** How many images it found. */
        get length() {
            return items.length;
        },
        /** Which one is showing. */
        get current() {
            return current;
        },
    };
}

export default createGallery;
